import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Link,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  FetchPayload,
  ItemType,
  MedicineSearch,
  MedicineSuggestion,
  SearchGeneric,
  SearchProduct,
  Suggestion,
} from "@interfaces";
import {
  Body,
  ContentContainer,
  DialogContent,
  Header,
  Modal,
  ModalStyleType,
  NavigateBeforeButton,
  scrollToTop,
  SearchHeader,
} from "@components";
import {
  SearchBar,
  MostPopularSearches as MostPopularSearchesBase,
} from "@components/Search";
import { MedicineSearchResults } from "./MedicineSearchResults";
import { useFetch, useMedicineSearchContext, useDocumentFlow } from "@contexts";
import { TemplatesSection } from "./components";
import { productSubCategoriesDetails } from "@utils";
import { mostSearchedMedicinesQueries, minimumSearchLength } from "@constants";
import { useDebouncedCallback } from "@hooks";
import { getProductsSuggestions } from "@services";

const StyledContainer = styled(ContentContainer)({
  flex: 1,
  justifyContent: "center",
});

const StyledBody = styled(Body)(({ theme }) => ({
  flex: 1,
  gap: 24,
  [theme.breakpoints.down("md")]: {
    padding: 0,
  },
}));

const ResultsContainer = styled(Body)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
  flex: 1,
}));

const MostPopularSearches = styled(MostPopularSearchesBase)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    width: "100vw",
  },
}));

const DesktopSearchBar = styled(SearchBar)({
  position: "static",
  maxWidth: "100%",
});

const MobileSearchBar = styled(SearchBar)({
  position: "static",
  maxWidth: "100%",
});

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.link,
  cursor: "pointer",
  textDecoration: "underline",
}));

const MostPopularSearchesTitle = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const DesktopLayoutContainer = styled(Box)({
  display: "flex",
  gap: "40px",
  justifyContent: "center",
  alignItems: "flex-start",
  width: "100%",
});

const DesktopPopularSearchesContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  justifyContent: "left",
  width: "100%",
}));

export function SearchMedicines() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const navigate = useNavigate();
  const fetch = useFetch();
  const flow = useDocumentFlow();

  const { searchText, setSearchText, searchResults, setSearchResults } =
    useMedicineSearchContext();
  const prevSearchText = useRef<string>(searchText);

  const [errorFetchingResults, setErrorFetchingResults] =
    useState<boolean>(false);
  const [preliminaryResults, setPreliminaryResults] = useState<Suggestion[]>(
    [],
  );

  const [selectedMedicineData, setSelectedMedicineData] =
    useState<MedicineSearch>();
  const [clickedProduct, setClickedProduct] = useState<SearchProduct>();

  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  const mobileSearchHadText = useRef(false);

  useEffect(() => {
    if (!isDesktop && mobileSearchActive) {
      if (searchText.length > 0) {
        mobileSearchHadText.current = true;
      } else if (mobileSearchHadText.current) {
        setMobileSearchActive(false);
        mobileSearchHadText.current = false;
      }
    }
  }, [searchText, mobileSearchActive, isDesktop]);

  const [loading, setLoading] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [showLowStockModal, setShowLowStockModal] = useState<boolean>(false);
  const [showCheckPrescriptionModal, setShowPrescriptionTypeCheckModal] =
    useState<boolean>(false);

  const wrappedSetSearchText = useCallback(
    (value: SetStateAction<string>) => {
      setSearchText(value);
      setErrorFetchingResults(false);
    },
    [setSearchText],
  );

  const parseAndFilterDuplicatedSuggestions = useCallback(
    (suggestions: MedicineSuggestion[]) => {
      const seenSuggestions = new Set<string>();
      return suggestions.reduce<MedicineSuggestion[]>((result, item) => {
        const parsedSuggestionText =
          item.activePrincipleCount >= 4 &&
          item.itemType === ItemType.GENERIC &&
          item.associatedProduct
            ? item.associatedProduct[0]
            : item.text;

        if (!seenSuggestions.has(parsedSuggestionText)) {
          result.push({ ...item, text: parsedSuggestionText });
          seenSuggestions.add(parsedSuggestionText);
        }
        return result;
      }, []);
    },
    [],
  );

  const search = useDebouncedCallback(async (text: string) => {
    prevSearchText.current = text;
    if (!text || text.trim().length < minimumSearchLength) return;
    const filteredSearchText = text.replace(/\bde\b/g, "");
    setSearching(true);
    setErrorFetchingResults(false);
    try {
      const params = new URLSearchParams();
      params.set("query", filteredSearchText.toLowerCase());

      // Get sorted suggestions
      const suggestions = await getProductsSuggestions(filteredSearchText);

      const medicineSuggestions: MedicineSuggestion[] = suggestions.hits.map(
        ({ text, itemType, activePrincipleCount, associatedProduct }) => ({
          text,
          itemType,
          activePrincipleCount,
          associatedProduct,
        }),
      );
      const parsedMedicineSuggestions =
        parseAndFilterDuplicatedSuggestions(medicineSuggestions);
      setPreliminaryResults(parsedMedicineSuggestions);
    } catch (error) {
      console.error(error);
      setPreliminaryResults([]);
      setErrorFetchingResults(true);
    }
    setSearching(false);
  }, 500);

  const handleSearch = useCallback(async () => {
    void search(searchText);
  }, [search, searchText]);

  useEffect(() => {
    if (searchText.trim().length < minimumSearchLength) {
      setPreliminaryResults([]);
    }
  }, [searchText]);

  const handleBack = useCallback(() => {
    if (selectedMedicineData !== undefined) {
      setSearchResults(undefined);
      setSelectedMedicineData(undefined);
      setLoading(false);
      scrollToTop();
    } else if (mobileSearchActive) {
      setMobileSearchActive(false);
      mobileSearchHadText.current = false;
      setSearchText("");
    } else {
      navigate(flow.routes.searchBackRoute);
    }
  }, [
    navigate,
    flow.routes.searchBackRoute,
    selectedMedicineData,
    setSearchResults,
    mobileSearchActive,
    setSearchText,
  ]);

  const onPreliminarySearchItemClick = useCallback(
    (item: Suggestion) => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("query", item.text.toLowerCase());
      setSearchText(item.text);
      fetch<FetchPayload<MedicineSearch>>("/search?" + params.toString())
        .then((res) => {
          setSelectedMedicineData({
            ...res.payload,
          });
          setSearchResults(res.payload);
        })
        .catch((error) => {
          console.log(error);
          setSelectedMedicineData({
            finalSearchResults: {
              searchType: ItemType.GENERIC,
              processedMedicines: [],
            },
            searchedQuery: "",
          });
          setSearchResults(undefined);
        })
        .finally(() => {
          setLoading(false);
          scrollToTop();
        });
    },
    [fetch, setSearchResults, setSearchText],
  );

  const onGenericMedicineClick = useCallback(
    (generic: SearchGeneric) => {
      if (generic.prescriptionType === "CHECK") {
        setShowPrescriptionTypeCheckModal(true);
        return;
      }
      navigate(flow.routes.treatmentRoute, {
        state: {
          medicine: generic,
        },
      });
    },
    [navigate, flow.routes.treatmentRoute],
  );

  const onMedicineClick = useCallback(
    (product: SearchProduct) => {
      const productSubCategories = productSubCategoriesDetails(product);
      const showCheckPrescriptionModalCondition =
        product.prescriptionType === "CHECK";
      const showLowStockModalCondition =
        !showLowStockModal &&
        (product.availability.status ?? "") === "SOLD_OUT" &&
        productSubCategories.isMedicine &&
        !productSubCategories.isInAnotherAllowedSubCategories;

      if (showCheckPrescriptionModalCondition) {
        setShowPrescriptionTypeCheckModal(true);
        return;
      }
      if (showLowStockModalCondition) {
        setClickedProduct(product);
        setShowLowStockModal(true);
        return;
      }

      navigate(flow.routes.treatmentRoute, {
        state: {
          medicine: product,
        },
      });
    },
    [navigate, flow.routes.treatmentRoute, showLowStockModal],
  );

  useEffect(() => {
    if (
      searchText.length === 0 ||
      searchText.length < prevSearchText.current.length
    ) {
      setSearchResults(undefined);

      setClickedProduct(undefined);
    }
    prevSearchText.current = searchText;
  }, [searchText, searchText.length, setSearchResults]);

  useEffect(() => {
    if (searchResults !== undefined) {
      setSelectedMedicineData(searchResults);

      scrollToTop();
    }
  }, [searchResults, selectedMedicineData]);

  const [prevScrollPos, setPrevScrollPos] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      if (currentScrollPos > prevScrollPos) {
        // Remove focus from input (to hide keyboard on mobile)
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
      setPrevScrollPos(currentScrollPos);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollPos]);

  const showEmptyResultsMessage =
    searchText.length >= minimumSearchLength &&
    !searching &&
    !errorFetchingResults &&
    prevSearchText.current === searchText.trim() &&
    preliminaryResults.length === 0;

  const showErrorResultsMessage = errorFetchingResults && !searching;

  const showLoader =
    searching &&
    preliminaryResults.length === 0 &&
    searchText.length >= minimumSearchLength;

  if (loading) {
    return (
      <>
        <SearchHeader
          searchText={searchText}
          setSearchText={setSearchText}
          onPreliminaryItemClick={onPreliminarySearchItemClick}
          preliminaryResults={preliminaryResults}
          search={handleSearch}
          handleBack={handleBack}
          locationState={{ attachedMedicines: selectedMedicineData }}
          placeholder="Busca nombre o principio activo"
          elevation={1}
        />
        <StyledContainer disableGutters>
          <StyledBody>
            <MedicineSearchResults.Skeleton />
          </StyledBody>
        </StyledContainer>
      </>
    );
  }

  return (
    <>
      {selectedMedicineData ||
      (!isDesktop && (searchText.length > 0 || mobileSearchActive)) ? (
        <SearchHeader
          searchText={searchText}
          setSearchText={wrappedSetSearchText}
          onPreliminaryItemClick={onPreliminarySearchItemClick}
          preliminaryResults={preliminaryResults}
          search={handleSearch}
          handleBack={handleBack}
          locationState={{ attachedMedicines: selectedMedicineData }}
          placeholder="Busca nombre o principio activo"
          autoFocus={!isDesktop && mobileSearchActive}
          elevation={1}
          showAddManuallyItemButton={
            showEmptyResultsMessage || showErrorResultsMessage
          }
          addManuallyItemText="Crear medicamento manualmente"
          showEmptyResultsMessage={showEmptyResultsMessage}
          showErrorResultsMessage={showErrorResultsMessage}
          showLoader={showLoader}
        />
      ) : !isDesktop ? (
        <Header
          title={flow.labels.mobileSearchTitle}
          titleColor="black"
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
      ) : (
        <Header
          useMekiDocIcon
          left={<NavigateBeforeButton onClick={handleBack} />}
        />
      )}
      <StyledContainer disableGutters>
        {!selectedMedicineData && !mobileSearchActive && (
          <DesktopLayoutContainer>
            <StyledBody>
              <>
                {isDesktop && (
                  <>
                    <Typography variant="h4">
                      {flow.labels.searchTitle}
                    </Typography>
                    <DesktopSearchBar
                      searchText={searchText}
                      setSearchText={wrappedSetSearchText}
                      onPreliminaryItemClick={onPreliminarySearchItemClick}
                      preliminaryResults={preliminaryResults}
                      search={handleSearch}
                      placeholder="Busca nombre o principio activo"
                      showAddManuallyItemButton={
                        showEmptyResultsMessage || showErrorResultsMessage
                      }
                      addManuallyItemText="Crear medicamento manualmente"
                      showEmptyResultsMessage={showEmptyResultsMessage}
                      showErrorResultsMessage={showErrorResultsMessage}
                      showLoader={showLoader}
                    />
                  </>
                )}
                {searchText.length === 0 &&
                  (isDesktop ? (
                    <DesktopPopularSearchesContainer>
                      <MostPopularSearchesTitle variant="body1">
                        Búsquedas Populares
                      </MostPopularSearchesTitle>
                      <MostPopularSearches
                        onItemClick={onPreliminarySearchItemClick}
                        mostSearchedQueries={mostSearchedMedicinesQueries}
                      />
                    </DesktopPopularSearchesContainer>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        width: "100%",
                      }}
                    >
                      <Box sx={{ paddingX: "16px", paddingTop: "16px" }}>
                        <MobileSearchBar
                          searchText={searchText}
                          setSearchText={wrappedSetSearchText}
                          onPreliminaryItemClick={onPreliminarySearchItemClick}
                          preliminaryResults={preliminaryResults}
                          search={handleSearch}
                          placeholder="Busca nombre o principio activo"
                          showSuggestions={false}
                          onFocus={() => {
                            setMobileSearchActive(true);
                            mobileSearchHadText.current = false;
                          }}
                        />
                      </Box>
                      <MostPopularSearches
                        onItemClick={onPreliminarySearchItemClick}
                        mostSearchedQueries={mostSearchedMedicinesQueries.slice(
                          0,
                          4,
                        )}
                      />
                      {flow.showTemplatesSection && (
                        <Box sx={{ padding: 2 }}>
                          <TemplatesSection maxItems={2} />
                        </Box>
                      )}
                    </Box>
                  ))}
              </>
            </StyledBody>
            {isDesktop && flow.showTemplatesSection && <TemplatesSection />}
          </DesktopLayoutContainer>
        )}

        {selectedMedicineData ? (
          <ResultsContainer>
            <MedicineSearchResults
              searchText={searchText}
              data={selectedMedicineData}
              onGenericMedicineClick={onGenericMedicineClick}
              onMedicineClick={onMedicineClick}
            />
          </ResultsContainer>
        ) : (
          <ResultsContainer>
            {loading && <MedicineSearchResults.Skeleton />}
          </ResultsContainer>
        )}

        {showLowStockModal && (
          <Modal
            title="Baja disponibilidad"
            content="Este medicamento está difícil de encontrar o agotado en este momento"
            isOpen={showLowStockModal}
            onClose={() => setShowLowStockModal(false)}
            buttonProps={{
              buttonText: "Continuar",
              onClick: () => {
                setShowLowStockModal(false);
                clickedProduct && onMedicineClick(clickedProduct);
              },
            }}
          />
        )}
        {showCheckPrescriptionModal && (
          <Modal
            title="No está permitido emitir recetas cheque electrónicas"
            isOpen={showCheckPrescriptionModal}
            onClose={() => setShowPrescriptionTypeCheckModal(false)}
            buttonProps={{
              buttonText: "Volver atrás",
              onClick: () => {
                setShowPrescriptionTypeCheckModal(false);
              },
            }}
            modalStyles={{
              title: "16px",
              type: ModalStyleType.CENTERED,
            }}
          >
            <DialogContent fontSize="14px">
              Puedes digitalizar recetas cheque físicas en{" "}
              {/* eslint-disable jsx-a11y/anchor-is-valid */}
              <StyledLink
                onClick={() =>
                  window.open(
                    "https://seremienlinea.minsal.cl/recetas/",
                    "_blank",
                  )
                }
              >
                https://seremienlinea.minsal.cl/recetas/
              </StyledLink>
            </DialogContent>
          </Modal>
        )}
      </StyledContainer>
    </>
  );
}
